import { useState, useEffect, useContext } from "react";
import jwtDecode from "jwt-decode";
import { prefixProtocol, postFetch } from "./utils";
import { AuthContext } from "./context";

const INTERVALS = {};
const HOSTS = { token: "uk.test.sustainabilitytool.com" };

export const getHost = () => {
  return HOSTS.token;
};

const setToken = (token, key = "token", refresh = false) => {
  localStorage.setItem(key, token);
  if (refresh) {
    setRefreshTimeout(key, token);
    window.dispatchEvent(new CustomEvent("token_updated", { detail: token }));
  }
};

const setRefreshTimeout = (key, token, buffer = 10) => {
  const timeout = getTokenExpiryTime(token) - buffer;
  console.log("Token will refresh in ", timeout, " seconds");
  if (INTERVALS[key]) {
    clearTimeout(INTERVALS[key]);
  }
  INTERVALS[key] = setTimeout(() => refreshToken(), timeout * 1000);
};

export const refreshToken = () => {
  const refresh = localStorage.getItem("token_refresh");
  console.log("Refreshing access token", refresh);
  postFetch({
    url: `/auth-jwt/refresh/`,
    data: { refresh: isValid(refresh) ? refresh : null },
    onSuccess: (data) => {
      console.log("refreshToken got response", data);
      setToken(data.access, "token", true);
    },
  });
};

export const verifyToken = (token) => {
  postFetch({
    url: `/auth-jwt/verify/`,
    data: { token },
    onSuccess: (data) => {
      console.log("Verify token got response", data);
      return data;
    },
  });
};

const getToken = (key = "token") => {
  const token = localStorage.getItem(key);
  if (isValid(token) && !INTERVALS.hasOwnProperty(key)) {
    setRefreshTimeout(key, token);
  }
  return token;
};

export const getNewTokenPair = (userpass, host, onFailure) => {
  postFetch({
    url: `/auth-jwt/get/`,
    data: userpass,
    onSuccess: (data) => {
      HOSTS.token = host;
      setToken(data.access, "token", true);
      setToken(data.refresh, "token_refresh");
    },
    onFailure,
  });
};

export const decodeToken = (token) => {
  try {
    return jwtDecode(token)
  } catch {
    return null
  }
}

export const getTokenExpiryTime = (token) => {
  const payload = decodeToken(token);
  const expiryTimestamp = payload?.exp || 0;
  const now = new Date().getTime();
  return expiryTimestamp - now / 1000;
};

export const isValid = (token, buffer = 0) => {
  return !!token && getTokenExpiryTime(token) > buffer;
};

export const signOut = () => {
  postFetch({
    url: `/auth-jwt/delete/`,
    data: {},
    onSuccess: (data) => {
      console.log("Logout success")
      localStorage.removeItem("token");
      localStorage.removeItem("token_refresh");
      Object.values(INTERVALS).map((i) => clearTimeout(i));
      window.dispatchEvent(new CustomEvent("token_updated", { detail: null }));
    },
    onFailure: () => console.log("Failure logging out"),
    method: "DELETE"
  });
};

// React hook for token and auth management
export const useToken = () => {
  const [token, setToken] = useState(getToken());
  const [company, setCompany] = useState({});
  const { host } = useContext(AuthContext);

  useEffect(() => {
    const tokenUpdateHandler = (e) => setToken(e.detail);
    window.addEventListener("token_updated", tokenUpdateHandler);
    return () =>
      window.removeEventListener("token_updated", tokenUpdateHandler);
  }, []);

  return {
    token,
    host: prefixProtocol(host),
    isAuthenticated: isValid(token),
    decodeToken,
    getToken: getNewTokenPair,
    getTokenExpiryTime,
    refreshToken,
    signIn: getNewTokenPair,
    signOut,
    company,
    setCompany,
  };
};

export default useToken;
